// src/components/AnalysisDialog.js
import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Slider, Paper, Table, TableContainer,
  TableHead, TableRow, TableCell, TableBody, Typography, IconButton, FormControl, InputLabel, Select, MenuItem, Grid, TextField
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// --- Các hàm tiện ích ---
const formatCurrency = (number) => {
  if (typeof number !== 'number') return 'N/A';
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(number);
};
const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp.seconds * 1000).toLocaleDateString('vi-VN');
};

// --- Component Card Thống Kê ---
const StatCard = ({ title, value, color }) => (
    <Paper elevation={3} sx={{ p: 2, textAlign: 'center', color: color || 'text.primary' }}>
        <Typography variant="h6">{value}</Typography>
        <Typography variant="subtitle2">{title}</Typography>
    </Paper>
);

// --- Component Tooltip tùy chỉnh cho biểu đồ ---
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper elevation={3} sx={{ p: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{label}</Typography>
        <Typography variant="caption" sx={{ color: '#8884d8' }}>
          Tồn kho: {payload[0].value}
        </Typography>
      </Paper>
    );
  }
  return null;
};

// --- Component chính ---
function AnalysisDialog({ open, onClose, products, onEdit, onDelete }) {
    const [stockFilter, setStockFilter] = useState('all');
    const [topN, setTopN] = useState(10);

    const maxQuantity = useMemo(() => (products.length === 0 ? 100 : Math.max(...products.map(p => Number(p.quantity || 0)), 0)), [products]);
    const [quantityRange, setQuantityRange] = useState([0, maxQuantity]);

    useEffect(() => { setQuantityRange([0, maxQuantity]); }, [maxQuantity]);

    // 1. Lọc sản phẩm theo các bộ lọc
    const filteredProducts = useMemo(() => {
        let tempProducts = products;
        switch (stockFilter) {
            case 'low': tempProducts = products.filter(p => p.quantity > 0 && p.quantity < 10); break;
            case 'out': tempProducts = products.filter(p => p.quantity <= 0); break;
            case 'high': tempProducts = products.filter(p => p.quantity > 100); break;
            default: break;
        }
        return tempProducts.filter(p => p.quantity >= quantityRange[0] && p.quantity <= quantityRange[1]);
    }, [products, quantityRange, stockFilter]);

    // 2. Thống kê động trên các sản phẩm đã lọc (Đã sửa lỗi)
    const dynamicStats = useMemo(() => ({
        count: filteredProducts.length,
        totalQuantity: filteredProducts.reduce((sum, p) => sum + Number(p.quantity || 0), 0),
        totalValue: filteredProducts.reduce((sum, p) => sum + (Number(p.quantity || 0) * Number(p.price || 0)), 0)
    }), [filteredProducts]);

    // 3. Chuẩn bị dữ liệu cho biểu đồ cột Top N
    const barChartData = useMemo(() => filteredProducts.sort((a, b) => b.quantity - a.quantity).slice(0, topN), [filteredProducts, topN]);

    // 4. Thống kê lịch sử cập nhật sản phẩm
    const updateHistoryData = useMemo(() => {
        const countsByDate = products.reduce((acc, p) => {
            if (p.updatedAt) {
                const date = formatDate(p.updatedAt);
                acc[date] = (acc[date] || 0) + 1;
            }
            return acc;
        }, {});
        return Object.entries(countsByDate).map(([date, count]) => ({ date, "Số sản phẩm đã cập nhật": count })).sort((a,b) => new Date(a.date.split('/').reverse().join('-')) - new Date(b.date.split('/').reverse().join('-')));
    }, [products]);
    
    const handleEditClick = (product) => {
        onEdit(product);
        onClose(); 
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
            <DialogTitle>Bảng Điều Khiển Phân Tích Tồn Kho</DialogTitle>
            <DialogContent>
                {/* Dòng 1: Các thẻ thống kê */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}><StatCard title="Số loại SP (đã lọc)" value={dynamicStats.count} /></Grid>
                    <Grid item xs={12} sm={6} md={3}><StatCard title="Tổng SL tồn (đã lọc)" value={dynamicStats.totalQuantity} /></Grid>
                    <Grid item xs={12} sm={6} md={3}><StatCard title="Tổng giá trị (đã lọc)" value={formatCurrency(dynamicStats.totalValue)} color="primary.main" /></Grid>
                    <Grid item xs={12} sm={6} md={3}><StatCard title="Tổng số SP trong kho" value={products.length} /></Grid>
                </Grid>

                {/* Dòng 2: Các biểu đồ */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} lg={7}>
                        <Typography variant="h6" gutterBottom>Lịch sử cập nhật sản phẩm</Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={updateHistoryData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis allowDecimals={false}/>
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="Số sản phẩm đã cập nhật" stroke="#82ca9d" strokeWidth={2} />
                            </LineChart>
                        </ResponsiveContainer>
                    </Grid>
                    <Grid item xs={12} lg={5}>
                         <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <Typography variant="h6" gutterBottom>Top sản phẩm tồn kho</Typography>
                             <TextField label="Hiển thị Top" type="number" size="small" value={topN} onChange={(e) => setTopN(Math.max(1, parseInt(e.target.value, 10) || 1))} sx={{width: 120}}/>
                         </Box>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={barChartData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" />
                                <YAxis type="category" dataKey="name" width={120} fontSize="12px" interval={0} />
                                <Tooltip cursor={{fill: 'rgba(206, 206, 206, 0.2)'}} content={<CustomTooltip/>}/>
                                <Legend />
                                <Bar dataKey="quantity" name="Số lượng" fill="#8884d8" />
                            </BarChart>
                        </ResponsiveContainer>
                    </Grid>
                </Grid>

                {/* Dòng 3: Bảng dữ liệu chi tiết */}
                <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Chi tiết sản phẩm</Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
                        <Grid item xs={12} md={4}>
                             <FormControl fullWidth>
                                <InputLabel>Trạng thái tồn kho</InputLabel>
                                <Select value={stockFilter} label="Trạng thái tồn kho" onChange={(e) => setStockFilter(e.target.value)}>
                                    <MenuItem value="all">Tất cả</MenuItem>
                                    <MenuItem value="high">Tồn kho nhiều (&gt;100)</MenuItem>
                                    <MenuItem value="low">Sắp hết hàng (&lt;10)</MenuItem>
                                    <MenuItem value="out">Hết hàng (&#8804;0)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={8}>
                             <Typography gutterBottom sx={{fontSize: '0.8rem'}}>Lọc theo khoảng tồn kho chi tiết</Typography>
                            <Slider value={quantityRange} onChange={(e, val) => setQuantityRange(val)} valueLabelDisplay="auto" min={0} max={maxQuantity} />
                        </Grid>
                    </Grid>
                    <TableContainer sx={{ maxHeight: 400 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Tên Sản Phẩm</TableCell>
                                    <TableCell align="right">Tồn Kho</TableCell>
                                    <TableCell align="right">Giá trị</TableCell>
                                    <TableCell align="right">Thao tác</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredProducts.map(p => (
                                    <TableRow key={p.id} hover>
                                        <TableCell>{p.name} ({p.sku})</TableCell>
                                        <TableCell align="right">{p.quantity}</TableCell>
                                        <TableCell align="right">{formatCurrency(p.quantity * p.price)}</TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => handleEditClick(p)}><EditIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" onClick={() => onDelete(p.id)}><DeleteIcon fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </DialogContent>
        </Dialog>
    );
}

export default AnalysisDialog;